import { FunctionComponent } from "react";

import { DropdownOption } from "../../../components/dropdown/Dropdown";
import { ConfirmTransferScreenProps } from "../useConfirmTransfer";
import TransitionWrapper from "../../../components/transition-wrapper/TransitionWrapper";
import ScrollableContainer from "../../../layout/scrollable-container";
import Container from "../../../layout/container";
import Footer from "../../../components/footer";
import Spacing from "../../../layout/spacing";
import validateName from "../../../utils/validateName";
import validateDigits from "../../../utils/validateDigits";
import { BodyLargeRegular, TitleLarge } from "../../../components/font";
import { Button, SearchableDropdown, SingleSelect, TextField, tokens } from "@manifesthhqinc/ui-library";
import { FormContainer } from "../../destination-account/screens/styles";
import HeadingContainer from "../../../layout/heading-container";
import formatBirthday from "../../../utils/formatBirthday";

const martialOptions: DropdownOption[] = [
  {
    label: "Married",
    value: "Married",
    id: "married",
  },
  {
    label: "Not Married",
    value: "Not Married",
    id: "not-married",
  },
];

const PersonalDetails: FunctionComponent<ConfirmTransferScreenProps> = (props: ConfirmTransferScreenProps) => {
  const {
    suffixList,
    userDetails,
    updateUserDetails,
    validateField,
    validation,
    citizenshipList,
    onClickContinue,
    userPhoneNumber,
    setUserPhoneNumber,
    validatePhoneNumber,
  } = props;

  const handleChange = (name: string, value: string) => {
    if ((name === "firstName" || name === "lastName" || name === "middleName") && validateName(value)) {
      updateUserDetails({ [name]: value });
    } else if (name === "ssnOrTin" && validateDigits(value)) {
      updateUserDetails({ [name]: value });
    }
  };

  const handleUpdatePhoneNumber = (e: string) => setUserPhoneNumber(e);

  return (
    <TransitionWrapper>
      <ScrollableContainer name="confirm-transfer-screen-personal-details">
        <Container>
          <HeadingContainer paneVariant="form" withProgressBar>
            <TitleLarge>You’re almost done! Verify your personal details</TitleLarge>
            <BodyLargeRegular $color={tokens.color.text.secondary}>
              Your provider needs this information to confirm your identity for transfers.
            </BodyLargeRegular>
          </HeadingContainer>

          <FormContainer>
            <TextField
              name="firstName"
              title="Legal first name*"
              value={userDetails.firstName}
              onChange={(e) => handleChange("firstName", e.target.value)}
              onBlur={() => validateField("firstName")}
              errorText={(validation?.firstName as string) || undefined}
              size="large"
            />

            <TextField
              value={userDetails.middleName}
              name="personal-details-middle-name"
              title="Middle name"
              onChange={(e) => handleChange("middleName", e.target.value)}
              size="large"
            />

            <TextField
              name="lastName"
              title="Last name*"
              value={userDetails.lastName}
              onChange={(e) => handleChange("lastName", e.target.value)}
              onBlur={() => validateField("lastName")}
              errorText={(validation?.lastName as string) || undefined}
              size="large"
            />
            <SingleSelect
              options={suffixList}
              onValueChange={(e) => updateUserDetails({ suffix: e })}
              name="suffix"
              title="Suffix"
              size="large"
            />
            <TextField
              isPhoneNumber
              isError={validation?.phoneNumber ? !!validation.phoneNumber : false}
              value={userPhoneNumber}
              name="personal-details-phone-number"
              title="Phone number*"
              onBlur={validatePhoneNumber}
              errorText={validation?.phoneNumber || undefined}
              placeholder="+1 (123) 456 7890"
              size="large"
              onPhoneNumberChange={handleUpdatePhoneNumber}
            />

            <TextField
              value={userDetails.dateOfBirth ? formatBirthday(userDetails.dateOfBirth) : ""}
              name="personal-details-user-date-of-birth"
              title="Date of birth*"
              type="date"
              onChange={(e) => updateUserDetails({ dateOfBirth: e.target.value })}
              onBlur={() => validateField("dateOfBirth")}
              errorText={(validation?.dateOfBirth as string) || undefined}
              isError={validation?.dateOfBirth !== "" && Boolean(validation?.dateOfBirth)}
              inputMode="numeric"
              size="large"
            />
            <TextField
              value={userDetails.ssnOrTin || ""}
              type="password"
              inputMode="numeric"
              name="personal-details-user-ssn"
              title="SSN or TIN*"
              onChange={(e) => handleChange("ssnOrTin", e.target.value)}
              onBlur={() => validateField("ssnOrTin")}
              errorText={(validation?.ssnOrTin as string) || undefined}
              isError={Boolean(validation?.ssnOrTin)}
              size="large"
            />
            <SearchableDropdown
              options={citizenshipList}
              onChange={(e) => updateUserDetails({ citizenship: e?.value || "" })}
              name="personal-details-user-citizenship"
              title="Citizenship*"
              errorText={(validation?.citizenship as string) || undefined}
              value={userDetails.citizenship}
              size="large"
              dropdownOptions={{
                menuPlacement: "bottom",
              }}
            />
            <SingleSelect
              options={martialOptions}
              onValueChange={(e) => updateUserDetails({ maritalStatus: e })}
              name="personal-details-user-martial-status"
              title="Current marital status*"
              errorText={(validation?.maritalStatus as string) || undefined}
              isError={Boolean(validation?.maritalStatus)}
              value={userDetails.maritalStatus}
              size="large"
            />
          </FormContainer>
          <Spacing vertical="16" />
        </Container>
      </ScrollableContainer>
      <Footer>
        <Button name="continue" onClick={onClickContinue} size="large">
          Continue
        </Button>
      </Footer>
    </TransitionWrapper>
  );
};

export default PersonalDetails;
